import { Suspense, lazy, useEffect, type ReactNode } from "react";
import { createRootRoute, createRoute, createRouter, Outlet, useNavigate } from "@tanstack/react-router";
import { ArrowClockwise, CircleNotch, LockKey, WarningCircle } from "@phosphor-icons/react";
import type { Permission } from "@clubghor/auth";
import { useSession } from "./lib/session";
import { AppShell } from "./components/layout/AppShell";

/**
 * Router.
 *
 * Every route component is lazily imported, so each screen ships its own chunk.
 * That is the structural fix for the previous admin, which put roughly 1 MB of
 * JavaScript on its UNAUTHENTICATED login page: a 954 KB charting bundle, a UI
 * kit, and a datepicker nothing referenced, all eager and all on every route.
 *
 * Routing is code-based rather than file-based: there is no codegen step, and the
 * whole route table is readable in one place.
 */

const LoginPage = lazy(() => import("./pages/Login"));
const DashboardPage = lazy(() => import("./pages/Dashboard"));
const OrdersPage = lazy(() => import("./pages/Orders"));
const OrderDetailPage = lazy(() => import("./pages/OrderDetail"));
const ProductsPage = lazy(() => import("./pages/Products"));
const ProductEditorPage = lazy(() => import("./pages/ProductEditor"));
const CategoriesPage = lazy(() => import("./pages/Categories"));
const CouponsPage = lazy(() => import("./pages/Coupons"));
const CustomersPage = lazy(() => import("./pages/Customers"));
const DeliveryAreasPage = lazy(() => import("./pages/DeliveryAreas"));
const MediaPage = lazy(() => import("./pages/Media"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const StaffPage = lazy(() => import("./pages/Staff"));
const ProfilePage = lazy(() => import("./pages/Profile"));
const DesignSystemPage = lazy(() => import("./pages/DesignSystem"));

/** Route-level fallback. Deliberately quiet: a spinner on every navigation is noise. */
function RouteFallback() {
  return (
    <div className="grid min-h-[60dvh] place-items-center" role="status" aria-live="polite">
      <CircleNotch className="size-5 animate-spin text-[var(--cg-content-muted)]" aria-hidden="true" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: () => (
    <div className="grid min-h-[100dvh] place-items-center px-6 text-center">
      <div>
        <p className="text-[0.6875rem] font-bold uppercase tracking-[0.14em] text-[var(--cg-content-subtle)]">
          404
        </p>
        <h1 className="mt-2 font-display text-xl font-semibold text-[var(--cg-content)]">
          That screen does not exist
        </h1>
        <p className="mt-1 text-[0.8125rem] text-[var(--cg-content-muted)]">
          Check the address, or use the sidebar to find your way back.
        </p>
        <a
          href="/"
          className="mt-5 inline-flex h-9 items-center rounded-[var(--admin-radius-field)] bg-[var(--cg-house)] px-4 text-[0.8125rem] font-medium text-[var(--cg-house-foreground)]"
        >
          Back to the dashboard
        </a>
      </div>
    </div>
  ),
});

/**
 * Sign-in route. Kept outside the authed layout so it renders without the app
 * shell and without loading any of the admin screens.
 */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => (
    <Lazy>
      <LoginPage />
    </Lazy>
  ),
});

/**
 * Auth gate.
 *
 * A component guard rather than `beforeLoad`, because the session lives in a
 * query above the router and is not part of the router's context. Placing it
 * ABOVE the Suspense boundary means an unauthenticated visitor never downloads a
 * page chunk: the Outlet is not rendered, so the lazy import never starts.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, isAuthError, refresh } = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    // Only a 401 means "signed out". Bouncing on ANY /me failure used to eject a
    // signed-in operator to /login during a network blip or a 5xx, and the failed
    // query then sat there unretried.
    if (!isLoading && !isAuthenticated && isAuthError) {
      void navigate({ to: "/login", replace: true });
    }
  }, [isAuthenticated, isLoading, isAuthError, navigate]);

  // While resolving, and while redirecting, show the quiet fallback rather than
  // flashing the shell with empty data.
  if (isLoading) return <RouteFallback />;
  if (!isAuthenticated) {
    if (isAuthError) return <RouteFallback />;
    // A non-auth failure: offer a retry instead of a redirect to /login.
    return (
      <div className="grid min-h-[60dvh] place-items-center px-6 text-center">
        <div>
          <WarningCircle className="mx-auto size-6 text-[var(--cg-content-muted)]" aria-hidden="true" />
          <h1 className="mt-3 font-display text-lg font-semibold text-[var(--cg-content)]">
            The admin could not load
          </h1>
          <p className="mt-1 text-[0.8125rem] text-[var(--cg-content-muted)]">
            We could not verify your session. Check your connection and try again.
          </p>
          <button
            type="button"
            onClick={refresh}
            className="mt-5 inline-flex h-9 items-center gap-2 rounded-[var(--admin-radius-field)] bg-[var(--cg-house)] px-4 text-[0.8125rem] font-medium text-[var(--cg-house-foreground)]"
          >
            <ArrowClockwise className="size-4" aria-hidden="true" />
            Try again
          </button>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * Guarded layout. Every admin screen renders inside this.
 */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authed",
  component: () => (
    <RequireAuth>
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </RequireAuth>
  ),
});

/**
 * Route-level permission gate.
 *
 * `can()` was previously used only to hide navigation, so deep-linking to a
 * screen your role cannot read gave you the full shell plus a bare 403 from the
 * API, with no explanation of why. Every route now declares the permission it
 * needs, and the mismatch is explained instead of leaking through as an error.
 *
 * This is a UX guard only. The API re-checks every request server-side; hiding a
 * screen is not the security boundary.
 */
function guarded(permission: Permission, Component: React.ComponentType) {
  return function GuardedRoute() {
    const { can } = useSession();
    if (!can(permission)) return <NoAccess permission={permission} />;
    return <Component />;
  };
}

function NoAccess({ permission }: { permission: Permission }) {
  const { user } = useSession();
  return (
    <AppShell title="Not available">
      <div className="surface mx-auto max-w-lg px-6 py-12 text-center">
        <span className="mx-auto grid size-11 place-items-center rounded-full bg-[var(--admin-warning-tint)] text-[var(--color-sun-900)] dark:text-[var(--color-sun-300)]">
          <LockKey className="size-5" weight="fill" aria-hidden="true" />
        </span>
        <h2 className="mt-4 font-display text-lg font-semibold text-[var(--cg-content)]">
          Your role cannot open this
        </h2>
        <p className="mx-auto mt-2 max-w-sm text-[0.8125rem] text-[var(--cg-content-muted)]">
          {user?.roleLabel ?? "Your role"} does not include the <code className="font-mono">{permission}</code>{" "}
          permission. An owner can grant it from Staff accounts.
        </p>
        <a
          href="/"
          className="mt-5 inline-flex h-9 items-center rounded-[var(--admin-radius-field)] bg-[var(--cg-house)] px-4 text-[0.8125rem] font-medium text-[var(--cg-house-foreground)]"
        >
          Back to the dashboard
        </a>
      </div>
    </AppShell>
  );
}

const dashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/",
  component: guarded("dashboard.read", DashboardPage),
});
const ordersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/orders",
  component: guarded("orders.read", OrdersPage),
});
const orderDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/orders/$id",
  component: guarded("orders.read", OrderDetailPage),
});
const productsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/products",
  component: guarded("products.read", ProductsPage),
});
const productNewRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/products/new",
  component: guarded("products.write", ProductEditorPage),
});
const productEditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/products/$id",
  component: guarded("products.read", ProductEditorPage),
});
const categoriesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/categories",
  component: guarded("categories.read", CategoriesPage),
});
const couponsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/coupons",
  component: guarded("coupons.read", CouponsPage),
});
const customersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/customers",
  component: guarded("customers.read", CustomersPage),
});
const deliveryRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/delivery-areas",
  component: guarded("delivery.read", DeliveryAreasPage),
});
const mediaRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/media",
  // Matches the API, which gates listing on media.write because no media.read
  // permission exists yet.
  component: guarded("media.write", MediaPage),
});
const settingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/settings",
  component: guarded("settings.read", SettingsPage),
});
const staffRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/staff",
  component: guarded("users.manage", StaffPage),
});
const profileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/profile",
  component: guarded("dashboard.read", ProfilePage),
});
/* Component reference for reviewing UI changes. Any signed-in staff member can open it. */
const designSystemRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/design-system",
  component: guarded("dashboard.read", DesignSystemPage),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  authedRoute.addChildren([
    dashboardRoute,
    ordersRoute,
    orderDetailRoute,
    productsRoute,
    productNewRoute,
    productEditRoute,
    categoriesRoute,
    couponsRoute,
    customersRoute,
    deliveryRoute,
    mediaRoute,
    settingsRoute,
    staffRoute,
    profileRoute,
    designSystemRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
